import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button } from '../components';
import {
  CheckCircle,
  XCircle,
  Clock,
  Download,
  User,
  Building2,
  Shield,
  ArrowLeft,
  Eye,
  EyeOff,
  ExternalLink
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { resolveRegisterLinks } from '../constants/registerLinks';

interface ScreeningRegistration {
  id: number;
  professional_type: string;
  is_registered: boolean;
  registration_number: string | null;
  registration_organization: string | null;
  registration_proof_url: string | null;
  is_required: boolean;
  is_approved: boolean;
  /** Automatische BIG-check (migratie 168) */
  big_check_status?: string | null;
  big_check_detail?: string | null;
  big_last_checked_at?: string | null;
  /** Handmatig traject (migratie 171): door de professional opgegeven of bevestigde einddatum */
  geldig_tot?: string | null;
  geen_vervaldatum?: boolean;
}

interface Screening {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  kvk_nummer: string;
  bedrijfsnaam: string;
  selected_professional_types: string | string[]; // Can be both string (JSON) or array
  insurance_company: string;
  coverage_amount: number;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  kvk_uittreksel_url: string | null;
  policy_document_url: string | null;
  kvk_approved: boolean;
  insurance_approved: boolean;
  registrations_approved: boolean;
  registrations?: ScreeningRegistration[];
  sla_deadline?: string;
  sla_overdue?: boolean;
}

// Vaste afwijsredenen — codes moeten gelijk zijn aan SCREENING_REJECTION_REASONS
// in src/types/screening.ts (server valideert hierop).
type RejectionReason = 'insurance_invalid' | 'company_mismatch' | 'registration_unverified';


// Uploads worden relatief opgeslagen (uploads/professional-screening/...);
// normaliseer naar absolute same-origin URL.
const docUrl = (url: string) => `/${String(url).replace(/^\/+/, '')}`;

/**
 * Live SLA-countdown voor de 4-uurs fast-track garantie.
 * Groen < 2u verstreken, oranje 2-3u, rood > 3u of verlopen.
 */
const SlaCountdown: React.FC<{ screening: Screening; now: number }> = ({ screening, now }) => {
  const { t } = useTranslation('pages');
  if (!screening.sla_deadline || !screening.submitted_at) return null;

  const deadline = new Date(screening.sla_deadline).getTime();
  const submitted = new Date(screening.submitted_at).getTime();
  const remainingMs = deadline - now;
  const elapsedHours = (now - submitted) / (60 * 60 * 1000);
  const overdue = screening.sla_overdue || remainingMs <= 0;

  const colorClass = overdue
    ? 'bg-red-100 text-red-800'
    : elapsedHours >= 3
      ? 'bg-red-100 text-red-800'
      : elapsedHours >= 2
        ? 'bg-orange-100 text-orange-800'
        : 'bg-green-100 text-green-800';

  const totalMinutes = Math.max(0, Math.floor(remainingMs / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const timeLabel = hours > 0 ? `${hours}u ${minutes}m` : `${minutes}m`;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}
      title={`SLA-deadline: ${new Date(screening.sla_deadline).toLocaleString('nl-NL')}`}
    >
      <Clock className="w-4 h-4 mr-1" />
      {overdue
        ? t('admin.screening.slaOverdue')
        : t('admin.screening.slaRemaining', { time: timeLabel })}
    </span>
  );
};

/**
 * Inline documentpreview: PDF in iframe, afbeeldingen in <img>.
 * /uploads is same-origin (nginx proxy), geen Content-Disposition → rendert inline.
 */
const DocumentPreview: React.FC<{ url: string; linkLabel: string }> = ({ url, linkLabel }) => {
  const { t } = useTranslation('pages');
  const [open, setOpen] = useState(false);
  const href = docUrl(url);
  const isPdf = /\.pdf$/i.test(href);

  return (
    <div className="mt-2">
      <div className="flex items-center space-x-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
        >
          {open ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
          {open ? t('admin.screening.closePreview') : t('admin.screening.preview')}
        </button>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
        >
          <Download className="w-4 h-4 mr-1" />
          {linkLabel}
        </a>
      </div>
      {open && (
        <div className="mt-2 border border-gray-200 rounded-md overflow-hidden bg-gray-100">
          {isPdf ? (
            <iframe src={href} title={linkLabel} className="w-full" style={{ height: '500px' }} />
          ) : (
            <img src={href} alt={linkLabel} className="max-w-full max-h-[500px] mx-auto" />
          )}
        </div>
      )}
    </div>
  );
};

interface DialogState {
  mode: 'approve' | 'reject';
  screening: Screening;
}

export const AdminScreeningReviewPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Tik elke 30s zodat de SLA-countdown live aftelt
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  // Approve/reject dialoog (vervangt window.prompt)
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [feedback, setFeedback] = useState('');
  // Geldigheids-Token (Continuous Vetting): einddatum polis/registratie, per sleutel
  // 'insurance:<screeningId>' of 'reg:<registratieId>'
  const [geldigTot, setGeldigTot] = useState<Record<string, string>>({});
  const [geenVervaldatum, setGeenVervaldatum] = useState<Record<number, boolean>>({});
  const [rejectReason, setRejectReason] = useState<RejectionReason>('insurance_invalid');

  // Fetch pending screenings
  const { data: screeningsData, isLoading } = useQuery({
    queryKey: ['admin-screenings'],
    queryFn: async () => {
      const response = await api.get('/admin/screening/screenings?status=pending');
      return response.data.data as Screening[];
    },
  });

  // Vanuit de admin-notificatie (?screening=<id>): naar die kaart scrollen
  const doelScreening = useSearchParams()[0].get('screening');
  useEffect(() => {
    if (!doelScreening || !screeningsData) return;
    document.getElementById(`screening-${doelScreening}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [doelScreening, screeningsData]);

  // Approve/Reject single document mutation
  const approveDocumentMutation = useMutation({
    mutationFn: async (payload: {
      screeningId: string;
      document_type: 'kvk' | 'insurance' | 'registration';
      approved: boolean;
      registration_id?: number;
      expires_at?: string;
      geen_vervaldatum?: boolean;
    }) => {
      const { screeningId, ...body } = payload;
      const response = await api.put(`/admin/screening/screenings/${screeningId}/approve-document`, body);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('admin.screening.docStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['admin-screenings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('admin.screening.docStatusError'));
    },
  });

  // Approve screening mutation
  const approveMutation = useMutation({
    mutationFn: async ({screeningId, feedback}: {screeningId: string, feedback: string}) => {
      const response = await api.put(`/admin/screening/screenings/${screeningId}/review`, {
        status: 'approved',
        admin_feedback: feedback
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('admin.screening.approvedSuccess'));
      setDialog(null);
      setFeedback('');
      queryClient.invalidateQueries({ queryKey: ['admin-screenings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('admin.screening.approvedError'));
    },
  });

  // Reject screening mutation
  const rejectMutation = useMutation({
    mutationFn: async ({screeningId, feedback, rejectionReason}: {screeningId: string, feedback: string, rejectionReason: RejectionReason}) => {
      const response = await api.put(`/admin/screening/screenings/${screeningId}/review`, {
        status: 'rejected',
        rejection_reason: rejectionReason,
        admin_feedback: feedback
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('admin.screening.rejectedSuccess'));
      setDialog(null);
      setFeedback('');
      queryClient.invalidateQueries({ queryKey: ['admin-screenings'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('admin.screening.rejectedError'));
    },
  });

  // Approve-gate: alle documenten individueel afgevinkt?
  const gateStatus = (screening: Screening) => {
    const requiredRegs = (screening.registrations || []).filter((r) => r.is_required);
    const missingRegs = requiredRegs.filter((r) => !r.is_approved);
    return {
      kvkOk: !!screening.kvk_approved,
      insuranceOk: !!screening.insurance_approved,
      regsOk: missingRegs.length === 0,
      complete: !!screening.kvk_approved && !!screening.insurance_approved && missingRegs.length === 0,
    };
  };

  const openDialog = (mode: 'approve' | 'reject', screening: Screening) => {
    setFeedback('');
    setRejectReason('insurance_invalid');
    setDialog({ mode, screening });
  };

  const submitDialog = () => {
    if (!dialog) return;
    if (dialog.mode === 'approve') {
      approveMutation.mutate({
        screeningId: dialog.screening.id,
        feedback: feedback.trim() || t('admin.screening.approvedDefault'),
      });
    } else {
      // Dropdown-reden is verplicht; de vrije toelichting is optioneel.
      rejectMutation.mutate({
        screeningId: dialog.screening.id,
        feedback: feedback.trim(),
        rejectionReason: rejectReason,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('admin.screening.loading')}</p>
        </div>
      </div>
    );
  }

  const screenings = screeningsData || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/admin')}
          className="flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('admin.screening.backToAdmin')}
        </Button>
      </div>

      {screenings.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('admin.screening.noScreenings')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('admin.screening.allReviewed')}</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {screenings.map((screening) => {
            // Handle both string and array formats for selected_professional_types
            const selectedTypes = typeof screening.selected_professional_types === 'string'
              ? JSON.parse(screening.selected_professional_types || '[]')
              : (screening.selected_professional_types || []);

            const gate = gateStatus(screening);

            return (
              <div key={screening.id} id={`screening-${screening.id}`} className="scroll-mt-4">
              <Card className={`overflow-hidden ${doelScreening === String(screening.id) ? 'ring-2 ring-[#0B1F3A]' : ''}`}>
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {screening.user_name}
                        </h3>
                        <p className="text-sm text-gray-600">{screening.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SlaCountdown screening={screening} now={now} />
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                        <Clock className="w-4 h-4 mr-1" />
                        {t('admin.screening.pending')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6 space-y-6">
                  {/* Company Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-start">
                        <Building2 className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">{t('admin.screening.companyInfo')}</h4>
                          <dl className="space-y-1 text-sm">
                            <div>
                              <dt className="inline text-gray-500">{t('admin.screening.companyName')}</dt>
                              <dd className="inline text-gray-900 font-medium">{screening.bedrijfsnaam}</dd>
                            </div>
                            <div>
                              <dt className="inline text-gray-500">{t('admin.screening.kvk')}</dt>
                              <dd className="inline text-gray-900 font-medium">{screening.kvk_nummer}</dd>
                            </div>
                          </dl>
                          {screening.kvk_uittreksel_url && (
                            <DocumentPreview
                              url={screening.kvk_uittreksel_url}
                              linkLabel={t('admin.screening.viewKvk')}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 text-gray-400 mt-0.5 mr-3" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-2">{t('admin.screening.insurance')}</h4>
                          <dl className="space-y-1 text-sm">
                            <div>
                              <dt className="inline text-gray-500">{t('admin.screening.insurer')}</dt>
                              <dd className="inline text-gray-900 font-medium">{screening.insurance_company}</dd>
                            </div>
                            <div>
                              <dt className="inline text-gray-500">{t('admin.screening.coverage')}</dt>
                              <dd className="inline text-gray-900 font-medium">
                                €{Number(screening.coverage_amount).toLocaleString('nl-NL')}
                              </dd>
                            </div>
                          </dl>
                          {screening.policy_document_url && (
                            <DocumentPreview
                              url={screening.policy_document_url}
                              linkLabel={t('admin.screening.viewPolicy')}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Professional Types */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">{t('admin.screening.selectedFunctions')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTypes.map((type: string) => (
                        <span
                          key={type}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Document beoordeling */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 mb-1">{t('admin.screening.documentStatus')}</h4>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{t('admin.screening.kvkExtract')}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className={screening.kvk_approved ? 'border-green-300 text-green-700' : 'border-gray-300'}
                          onClick={() =>
                            approveDocumentMutation.mutate({
                              screeningId: screening.id,
                              document_type: 'kvk',
                              approved: !screening.kvk_approved,
                            })
                          }
                          disabled={approveDocumentMutation.isPending}
                        >
                          {screening.kvk_approved ? t('admin.screening.approved') : t('admin.screening.markApproved')}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{t('admin.screening.insurancePolicy')}</span>
                        <div className="flex items-center gap-2">
                        {!screening.insurance_approved && (
                          <input
                            type="date"
                            aria-label={t('admin.screening.validUntil')}
                            title={t('admin.screening.validUntil')}
                            value={geldigTot[`insurance:${screening.id}`] ?? ''}
                            onChange={(e) => setGeldigTot((g) => ({ ...g, [`insurance:${screening.id}`]: e.target.value }))}
                            className="rounded border border-gray-300 px-2 py-1 text-xs"
                          />
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className={screening.insurance_approved ? 'border-green-300 text-green-700' : 'border-gray-300'}
                          onClick={() =>
                            approveDocumentMutation.mutate({
                              screeningId: screening.id,
                              document_type: 'insurance',
                              approved: !screening.insurance_approved,
                              expires_at: (!screening.insurance_approved && geldigTot[`insurance:${screening.id}`]) || undefined,
                            })
                          }
                          disabled={approveDocumentMutation.isPending}
                        >
                          {screening.insurance_approved ? t('admin.screening.approved') : t('admin.screening.markApproved')}
                        </Button>
                        </div>
                      </div>
                    </div>

                    {/* Registraties per type */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900 mb-1">{t('admin.screening.registrationsPerFunction')}</h4>
                      {screening.registrations && screening.registrations.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {screening.registrations.map((reg) => {
                            const registerLinks = resolveRegisterLinks(reg.registration_organization);
                            return (
                              <div
                                key={reg.id}
                                className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {reg.professional_type}
                                    </p>
                                    {reg.registration_organization && (
                                      <p className="text-xs text-gray-600">
                                        {reg.registration_organization}{reg.is_required ? ` ${t('admin.screening.required')}` : ` ${t('admin.screening.recommended')}`}
                                      </p>
                                    )}
                                    {reg.registration_number && (
                                      <p className="text-xs text-gray-600">
                                        {t('admin.screening.registrationNumber')} {reg.registration_number}
                                      </p>
                                    )}
                                    {reg.big_check_status && (
                                      <p className={`text-xs font-medium ${reg.big_check_status === 'actief' ? 'text-green-700' : 'text-amber-700'}`}>
                                        {t('admin.screening.bigCheck')}: {reg.big_check_detail || reg.big_check_status}
                                        {reg.big_last_checked_at && ` (${new Date(reg.big_last_checked_at).toLocaleDateString('nl-NL')})`}
                                      </p>
                                    )}
                                    {registerLinks.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {registerLinks.map((link) => (
                                          <a
                                            key={link.url}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-xs text-blue-700 hover:bg-blue-100"
                                          >
                                            <ExternalLink className="w-3 h-3 mr-1" />
                                            {t('admin.screening.openRegister', { register: link.label })}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {reg.registration_proof_url && (
                                      <DocumentPreview
                                        url={reg.registration_proof_url}
                                        linkLabel={t('admin.screening.viewProof')}
                                      />
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-1 ml-2">
                                  {!reg.is_approved && reg.big_check_status !== 'actief' && (
                                    <>
                                      <input
                                        type="date"
                                        aria-label={t('admin.screening.validUntil')}
                                        title={t('admin.screening.validUntil')}
                                        value={geldigTot[`reg:${reg.id}`] ?? (reg.geldig_tot ? String(reg.geldig_tot).slice(0, 10) : '')}
                                        onChange={(e) => setGeldigTot((g) => ({ ...g, [`reg:${reg.id}`]: e.target.value }))}
                                        disabled={!!geenVervaldatum[reg.id]}
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                      />
                                      {reg.registration_proof_url && (
                                        <label className="flex items-center gap-1 text-xs text-gray-600">
                                          <input
                                            type="checkbox"
                                            checked={!!geenVervaldatum[reg.id]}
                                            onChange={(e) => setGeenVervaldatum((g) => ({ ...g, [reg.id]: e.target.checked }))}
                                          />
                                          {t('admin.screening.noExpiry')}
                                        </label>
                                      )}
                                    </>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className={reg.is_approved ? 'border-green-300 text-green-700' : 'border-gray-300'}
                                    onClick={() =>
                                      approveDocumentMutation.mutate({
                                        screeningId: screening.id,
                                        document_type: 'registration',
                                        approved: !reg.is_approved,
                                        registration_id: reg.id,
                                        expires_at: (!reg.is_approved && !geenVervaldatum[reg.id]
                                          && (geldigTot[`reg:${reg.id}`] ?? (reg.geldig_tot ? String(reg.geldig_tot).slice(0, 10) : ''))) || undefined,
                                        geen_vervaldatum: !reg.is_approved && !!geenVervaldatum[reg.id] ? true : undefined,
                                      })
                                    }
                                    disabled={approveDocumentMutation.isPending}
                                  >
                                    {reg.is_approved ? t('admin.screening.approved') : t('admin.screening.markApproved')}
                                  </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {t('admin.screening.noRegistrations')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Approve-gate checklist */}
                  <div className="border border-gray-200 rounded-md px-4 py-3 bg-gray-50">
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">{t('admin.screening.checklistTitle')}</h4>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className={`inline-flex items-center ${gate.kvkOk ? 'text-green-700' : 'text-gray-500'}`}>
                        {gate.kvkOk ? <CheckCircle className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                        {t('admin.screening.kvkExtract')}
                      </span>
                      <span className={`inline-flex items-center ${gate.insuranceOk ? 'text-green-700' : 'text-gray-500'}`}>
                        {gate.insuranceOk ? <CheckCircle className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                        {t('admin.screening.insurancePolicy')}
                      </span>
                      <span className={`inline-flex items-center ${gate.regsOk ? 'text-green-700' : 'text-gray-500'}`}>
                        {gate.regsOk ? <CheckCircle className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                        {t('admin.screening.registrationsPerFunction')}
                      </span>
                    </div>
                  </div>

                  {/* Submitted Date */}
                  <div className="text-sm text-gray-500">
                    {t('admin.screening.submittedAt')} {new Date(screening.submitted_at).toLocaleString('nl-NL', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => openDialog('reject', screening)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    disabled={rejectMutation.isPending || approveMutation.isPending}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {t('admin.screening.reject')}
                  </Button>
                  <span title={gate.complete ? undefined : t('admin.screening.gateBlockedTooltip')}>
                    <Button
                      onClick={() => openDialog('approve', screening)}
                      disabled={!gate.complete || rejectMutation.isPending || approveMutation.isPending}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('admin.screening.approve')}
                    </Button>
                  </span>
                </div>
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve/Reject dialoog */}
      {dialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {dialog.mode === 'approve'
                ? t('admin.screening.approveTitle')
                : t('admin.screening.rejectTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {dialog.screening.user_name} — {dialog.screening.bedrijfsnaam}
            </p>
            {dialog.mode === 'reject' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.screening.rejectReasonLabel')}
                </label>
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value as RejectionReason)}
                  autoFocus
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="insurance_invalid">{t('admin.screening.rejectReasonInsuranceInvalid')}</option>
                  <option value="company_mismatch">{t('admin.screening.rejectReasonCompanyMismatch')}</option>
                  <option value="registration_unverified">{t('admin.screening.rejectReasonRegistrationUnverified')}</option>
                </select>
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {dialog.mode === 'approve'
                ? t('admin.screening.noteOptional')
                : t('admin.screening.rejectFeedbackLabel')}
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              maxLength={500}
              autoFocus={dialog.mode === 'approve'}
              placeholder={dialog.mode === 'reject' ? t('admin.screening.rejectFeedbackPlaceholder') : undefined}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                variant="outline"
                onClick={() => { setDialog(null); setFeedback(''); }}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                {t('admin.screening.cancel')}
              </Button>
              {dialog.mode === 'approve' ? (
                <Button
                  onClick={submitDialog}
                  disabled={approveMutation.isPending}
                  loading={approveMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('admin.screening.confirmApprove')}
                </Button>
              ) : (
                <Button
                  onClick={submitDialog}
                  disabled={rejectMutation.isPending}
                  loading={rejectMutation.isPending}
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {t('admin.screening.confirmReject')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
